/**
 * Pokemon Evolution Service
 *
 * Handles evolution logic including:
 * - Checking available evolutions for a Pokemon
 * - Validating evolution conditions
 * - Executing evolution (updating Pokemon data)
 */

import { POKE_DATA } from "game/data/pokemon/data";
import { PokeData, EvolutionTriggerName } from "types/pokemon/poke-data";
import { PokeNames } from "types/pokemon/poke-names";
import { ITEM_NAME } from "game/data/items/item-names";

/**
 * Maps target species to the item required for their trade evolution.
 * This is necessary because the generated data currently has held_item_name: null
 * for these evolutions.
 */
const TRADE_ITEM_MAP: Record<string, ITEM_NAME | null> = {
  // Gen 1
  alakazam: null,
  machamp: null,
  golem: null,
  gengar: null,

  // Gen 2
  politoed: ITEM_NAME.KINGS_ROCK,
  slowking: ITEM_NAME.KINGS_ROCK,
  steelix: ITEM_NAME.METAL_COAT,
  scizor: ITEM_NAME.METAL_COAT,
  kingdra: ITEM_NAME.DRAGON_SCALE,
  porygon2: ITEM_NAME.UPGRADE,

  // Gen 3
  huntail: ITEM_NAME.DEEP_SEA_TOOTH,
  gorebyss: ITEM_NAME.DEEP_SEA_SCALE,
  milotic: ITEM_NAME.PRISM_SCALE,

  // Gen 4
  rhyperior: ITEM_NAME.PROTECTOR,
  electivire: ITEM_NAME.ELECTIRIZER,
  magmortar: ITEM_NAME.MAGMARIZER,
  "porygon-z": ITEM_NAME.DUBIOUS_DISC,
  dusknoir: ITEM_NAME.REAPER_CLOTH,

  // Gen 5
  gigalith: null,
  conkeldurr: null,

  // Gen 6
  aromatisse: ITEM_NAME.SACHET,
  slurpuff: ITEM_NAME.WHIPPED_DREAM,
  trevenant: null,
  gourgeist: null,
};

/**
 * Represents a Pokemon instance from the database
 */
export interface OwnedPokemon {
  id: number;
  base_pokemon_name: string;
  level: number;
  is_traded?: boolean; // Flag set after receiving via trade
}

/**
 * Represents a possible evolution option
 */
export interface EvolutionOption {
  target_species: string;
  trigger_type: EvolutionTriggerName;
  required_item?: string | null;
  required_level?: number | null;
  is_player_choice?: boolean; // For time_of_day, location, branched evolutions
  choice_label?: string; // Label for the choice (e.g., "Day", "Night", "Forest")
  is_ready: boolean;
}

/**
 * Maps evolution stone names from PokeAPI format to our ITEM_NAME enum
 */
const STONE_NAME_MAP: Record<string, ITEM_NAME> = {
  "fire-stone": ITEM_NAME.FIRE_STONE,
  "water-stone": ITEM_NAME.WATER_STONE,
  "thunder-stone": ITEM_NAME.THUNDER_STONE,
  "leaf-stone": ITEM_NAME.LEAF_STONE,
  "ice-stone": ITEM_NAME.ICE_STONE,
  "moon-stone": ITEM_NAME.MOON_STONE,
  "sun-stone": ITEM_NAME.SUN_STONE,
  "dusk-stone": ITEM_NAME.DUSK_STONE,
  "dawn-stone": ITEM_NAME.DAWN_STONE,
  "shiny-stone": ITEM_NAME.SHINY_STONE,
  "tart-apple": ITEM_NAME.TART_APPLE,
  "sweet-apple": ITEM_NAME.SWEET_APPLE,
  "syrupy-apple": ITEM_NAME.SYRUPY_APPLE,
};

/**
 * Level threshold to use when converting happiness-based evolutions
 */
const HAPPINESS_LEVEL_THRESHOLD = 20;

/**
 * Gets the PokeData for a given Pokemon name
 */
export const getPokeData = (pokemonName: string): PokeData | null => {
  const enumKey = pokemonName
    .toUpperCase()
    .replace(/-/g, "_") as keyof typeof PokeNames;
  const pokeNameValue = PokeNames[enumKey];
  if (!pokeNameValue) return null;
  return POKE_DATA[pokeNameValue] || null;
};

/**
 * Gets all available evolution options for a Pokemon
 * Returns empty array if Pokemon cannot evolve
 */
export const getAvailableEvolutions = (
  pokemon: OwnedPokemon,
  playerItems?: string[],
): EvolutionOption[] => {
  const pokeData = getPokeData(pokemon.base_pokemon_name);
  if (!pokeData) return [];

  const evolutionChain = pokeData.evolution_chain;
  if (!evolutionChain) return [];

  // Find current species in the evolution chain
  const currentSpecies = evolutionChain.species.find(
    (s) => s.name === pokemon.base_pokemon_name,
  );
  if (!currentSpecies) return [];

  // Find species that evolve FROM the current Pokemon
  const possibleEvolutions = evolutionChain.species.filter(
    (s) => s.evolves_from_species_id === pokeData.id,
  );

  if (possibleEvolutions.length === 0) return [];

  const options: EvolutionOption[] = [];

  for (const evolution of possibleEvolutions) {
    const evolutions = evolution.pokemonevolutions;
    if (!evolutions || evolutions.length === 0) continue;

    for (const evo of evolutions) {
      const option = processEvolutionCondition(
        pokemon,
        evolution.name,
        evo,
        playerItems,
      );
      if (option) {
        options.push(option);
      }
    }
  }

  // Deduplicate options by target_species (keep first valid one per target)
  const uniqueOptions = new Map<string, EvolutionOption>();
  for (const opt of options) {
    if (!uniqueOptions.has(opt.target_species)) {
      uniqueOptions.set(opt.target_species, opt);
    }
  }

  return Array.from(uniqueOptions.values());
};

/**
 * Processes a single evolution condition and returns an EvolutionOption if valid
 */
const processEvolutionCondition = (
  pokemon: OwnedPokemon,
  targetSpecies: string,
  evo: NonNullable<
    PokeData["evolution_chain"]["species"][0]["pokemonevolutions"][0]
  >,
  playerItems?: string[],
): EvolutionOption | null => {
  const triggerName = evo.evolutiontrigger?.name;
  if (!triggerName) return null;

  // Handle USE_ITEM trigger (evolution stones)
  if (triggerName === EvolutionTriggerName.USE_ITEM && evo.item) {
    const itemName = STONE_NAME_MAP[evo.item.name] || evo.item.name;
    return {
      target_species: targetSpecies,
      trigger_type: EvolutionTriggerName.USE_ITEM,
      required_item: itemName,
      is_ready: playerItems?.includes(itemName) ?? false,
    };
  }

  // Handle TRADE trigger
  if (triggerName === EvolutionTriggerName.TRADE) {
    // Check our manual map first since data might be missing held_item_name
    const mappedItem =
      TRADE_ITEM_MAP[targetSpecies] ||
      TRADE_ITEM_MAP[targetSpecies.toLowerCase()];

    // Priority 1: Manual Map or Data-defined Item
    const requiredItem = mappedItem || evo.held_item_name;

    if (requiredItem) {
      return {
        target_species: targetSpecies,
        trigger_type: EvolutionTriggerName.USE_ITEM,
        required_item: requiredItem,
        is_ready: playerItems?.includes(requiredItem) ?? false,
      };
    }

    // Priority 2: Trade with no item -> Free Evolution
    return {
      target_species: targetSpecies,
      trigger_type: EvolutionTriggerName.LEVEL_UP,
      is_player_choice: true,
      choice_label: "Evolve",
      is_ready: true,
    };
  }

  // Handle LEVEL_UP trigger with various conditions
  if (triggerName === EvolutionTriggerName.LEVEL_UP) {
    // Min level check
    if (evo.min_level !== null && pokemon.level < evo.min_level) {
      return {
        target_species: targetSpecies,
        trigger_type: EvolutionTriggerName.LEVEL_UP,
        required_level: evo.min_level,
        is_ready: false,
      };
    }

    // Happiness-based: convert to level threshold
    if (evo.min_happiness !== null) {
      if (pokemon.level < HAPPINESS_LEVEL_THRESHOLD) {
        return {
          target_species: targetSpecies,
          trigger_type: EvolutionTriggerName.LEVEL_UP,
          required_level: HAPPINESS_LEVEL_THRESHOLD,
          is_ready: false,
        };
      }
    }

    // Beauty-based: require Prism Scale
    if (evo.min_beauty !== null) {
      return {
        target_species: targetSpecies,
        trigger_type: EvolutionTriggerName.USE_ITEM,
        required_item: ITEM_NAME.PRISM_SCALE,
        is_ready: playerItems?.includes(ITEM_NAME.PRISM_SCALE) ?? false,
      };
    }

    // Time of day: player choice
    if (evo.time_of_day && evo.time_of_day !== "") {
      const required_level = evo.min_level || HAPPINESS_LEVEL_THRESHOLD;
      return {
        target_species: targetSpecies,
        trigger_type: EvolutionTriggerName.LEVEL_UP,
        required_level,
        is_player_choice: true,
        choice_label: evo.time_of_day === "day" ? "Day" : "Night",
        is_ready: pokemon.level >= required_level,
      };
    }

    // Location-based: player choice
    if (evo.location) {
      return {
        target_species: targetSpecies,
        trigger_type: EvolutionTriggerName.LEVEL_UP,
        required_level: evo.min_level,
        is_player_choice: true,
        choice_label: formatLocationName(evo.location.name),
        is_ready: pokemon.level >= (evo.min_level ?? 0),
      };
    }

    // Relative physical stats (Tyrogue): player choice
    if (evo.relative_physical_stats !== null) {
      const label = getRelativeStatsLabel(evo.relative_physical_stats);
      return {
        target_species: targetSpecies,
        trigger_type: EvolutionTriggerName.LEVEL_UP,
        required_level: evo.min_level,
        is_player_choice: true,
        choice_label: label,
        is_ready: pokemon.level >= (evo.min_level ?? 0),
      };
    }

    // Turn upside down (Inkay): just use level
    if (evo.turn_upside_down) {
      const required_level = evo.min_level || 30;

      return {
        target_species: targetSpecies,
        trigger_type: EvolutionTriggerName.LEVEL_UP,
        required_level,
        is_ready: pokemon.level >= required_level,
      };
    }

    // Affection-based: convert to level threshold
    if (evo.min_affection !== null) {
      const required_level = evo.min_affection || HAPPINESS_LEVEL_THRESHOLD;
      return {
        target_species: targetSpecies,
        trigger_type: EvolutionTriggerName.LEVEL_UP,
        required_level,
        is_ready: pokemon.level >= required_level,
      };
    }

    // Move-based: player choice (we don't track moves learned)
    if (evo.move) {
      const required_level = evo.min_level || HAPPINESS_LEVEL_THRESHOLD;
      return {
        target_species: targetSpecies,
        trigger_type: EvolutionTriggerName.LEVEL_UP,
        is_player_choice: true,
        choice_label: `Knows ${formatMoveName(evo.move.name)}`,
        is_ready: pokemon.level >= required_level,
      };
    }

    // Type-based (Sylveon): player choice
    if (evo.type) {
      const required_level = evo.min_level || HAPPINESS_LEVEL_THRESHOLD;
      return {
        target_species: targetSpecies,
        trigger_type: EvolutionTriggerName.LEVEL_UP,
        is_player_choice: true,
        choice_label: `${evo.type} type`,
        is_ready: pokemon.level >= required_level,
      };
    }

    // Gender-based: player choice
    if (evo.gender) {
      const required_level = evo.min_level || HAPPINESS_LEVEL_THRESHOLD;
      return {
        target_species: targetSpecies,
        trigger_type: EvolutionTriggerName.LEVEL_UP,
        is_player_choice: true,
        choice_label: evo.gender.name === "female" ? "Female" : "Male",
        is_ready: pokemon.level >= required_level,
      };
    }
    const required_level = evo.min_level || HAPPINESS_LEVEL_THRESHOLD;
    // Simple level-up evolution
    return {
      target_species: targetSpecies,
      trigger_type: EvolutionTriggerName.LEVEL_UP,
      required_level,
      is_ready: pokemon.level >= required_level,
    };
  }

  // Handle OTHER trigger (special conditions like Annihilape, Gholdengo, etc.)
  if (triggerName === EvolutionTriggerName.OTHER) {
    const required_level = evo.min_level || 30; // Default to 30 for special Gen 9+ evolutions if not specified
    return {
      target_species: targetSpecies,
      trigger_type: EvolutionTriggerName.OTHER,
      required_level,
      is_player_choice: true,
      choice_label: "Special Condition",
      is_ready: pokemon.level >= required_level,
    };
  }

  return null;
};

/**
 * Formats a location name for display
 */
const formatLocationName = (locationName: string): string => {
  return locationName
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Formats a move name for display
 */
const formatMoveName = (moveName: string): string => {
  return moveName
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Gets a label for relative physical stats evolution (Tyrogue)
 */
const getRelativeStatsLabel = (relativeStats: number): string => {
  if (relativeStats > 0) return "Attack > Defense";
  if (relativeStats < 0) return "Defense > Attack";
  return "Attack = Defense";
};

/**
 * Checks if a Pokemon can evolve to a specific target
 */
export const canEvolve = (
  pokemon: OwnedPokemon,
  targetSpecies: string,
  playerItems?: string[],
): { canEvolve: boolean; reason?: string; requiredItem?: string } => {
  const options = getAvailableEvolutions(pokemon, playerItems);
  const targetOption = options.find(
    (opt) => opt.target_species === targetSpecies,
  );

  if (!targetOption) {
    return { canEvolve: false, reason: "Evolution not available" };
  }

  // Check level requirement
  if (
    targetOption.required_level &&
    pokemon.level < targetOption.required_level
  ) {
    return {
      canEvolve: false,
      reason: `Pokemon must be at least level ${targetOption.required_level}`,
    };
  }

  // Check item requirement
  if (targetOption.required_item) {
    if (!playerItems || !playerItems.includes(targetOption.required_item)) {
      return {
        canEvolve: false,
        reason: `Requires ${formatItemName(targetOption.required_item)}`,
        requiredItem: targetOption.required_item,
      };
    }
  }

  return { canEvolve: true };
};

/**
 * Formats an item name for display
 */
const formatItemName = (itemName: string): string => {
  return itemName
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Gets the new stats for an evolved Pokemon
 */
export const getEvolvedStats = (
  targetSpecies: string,
  currentLevel: number,
): { resistance: number; speed: number; ability_power: number } | null => {
  const targetData = getPokeData(targetSpecies);
  if (!targetData) return null;

  // Return base stats of the new species
  // Level scaling can be applied elsewhere if needed
  return {
    resistance: targetData.stats.resistance,
    speed: targetData.stats.speed,
    ability_power: targetData.stats.ability_power,
  };
};

/**
 * Gets the default moves for an evolved Pokemon
 */
export const getEvolvedMoves = (targetSpecies: string): string[] => {
  const targetData = getPokeData(targetSpecies);
  if (!targetData) return [];

  // Return the first 4 moves as default active skills
  return [...targetData.moves];
};

/**
 * Result of an evolution operation
 */
export interface EvolutionResult {
  success: boolean;
  error?: string;
  old_species?: string;
  new_species?: string;
  new_moves?: string[];
  consumed_item?: string;
}

/**
 * Validates and prepares evolution data (does not modify database)
 * Returns the data needed to perform the evolution
 */
export const prepareEvolution = (
  pokemon: OwnedPokemon,
  targetSpecies: string,
  playerItems: string[],
): EvolutionResult => {
  const canEvolveResult = canEvolve(pokemon, targetSpecies, playerItems);

  if (!canEvolveResult.canEvolve) {
    return {
      success: false,
      error: canEvolveResult.reason,
    };
  }

  const options = getAvailableEvolutions(pokemon, playerItems);
  const targetOption = options.find(
    (opt) => opt.target_species === targetSpecies,
  );

  if (!targetOption) {
    return {
      success: false,
      error: "Evolution option not found",
    };
  }

  const newMoves = getEvolvedMoves(targetSpecies);

  return {
    success: true,
    old_species: pokemon.base_pokemon_name,
    new_species: targetSpecies,
    new_moves: newMoves,
    consumed_item: targetOption.required_item || undefined,
  };
};

export { STONE_NAME_MAP, HAPPINESS_LEVEL_THRESHOLD };

// Export trade evolution utilities
export {
  checkTradeEvolutionEligibility,
  markPokemonAsTraded,
  promptTradeEvolution,
  TRADE_EVOLUTION_POKEMON,
  type TradedPokemon,
  type TradeEvolutionCheck,
} from "./trade-evolution-utils";
